"use client";
import apis from "@/configs/apis";
import searchtabData from "@/public/data/searchTab.json";
import Datepicker from "@/shared/components/common/datepicker/Datepicker";
import { DrawerClose } from "@/shared/components/common/drawer/drawer";
import { PopoverClose } from "@/shared/components/common/popover";
import { useDeviceType } from "@/shared/context/DeviceContext";
import useProgress from "@/shared/hooks/useProgress";
import { jalaliToGregorian } from "@/shared/libs/date";
import fetcher, { type ExtendedResponse } from "@/shared/libs/fetcher";
import { isNumber } from "@/shared/libs/type-utils";
import { createRoomsPattern } from "@/shared/libs/utils";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  CalendarBlank,
  CalendarCheck,
  CaretDown,
  MapPinSimpleArea,
} from "@phosphor-icons/react";
import { addDays } from "date-fns";
import { differenceInDays, format, parse } from "date-fns-jalali";
import { useRouter } from "next/navigation";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import useSWR from "swr";
import type { HotelSchema } from "../../../../../schemas/hotel/hotelTab.schema";
import {
  hotelDefaultValue,
  hotelSchema,
} from "../../../../../schemas/hotel/hotelTab.schema";

import LocationPicker, { type TCity } from "../../locationPicker";

import RoomPicker from "../../roomPicker/RoomPicker";
import Stayduration from "../../stayduration/Stayduration";
import TabContent from "../TabContent";

type TFormField = "checkin" | "checkout" | "city" | "rooms";

interface HotelTabProps {
  mobile?: boolean;
  city?: string;
  slug?: string;
  page?: "landing" | "result";
  searchParams?: SearchParams;
}

export default function HotelTab({
  mobile,
  page = "landing",
  searchParams,
  city,
  slug
}: Readonly<HotelTabProps>) {
  const userDeviceType = useDeviceType();
  const router = useRouter();

  const { checkIn, checkOut, rooms } = searchParams ?? {};
  const [search, setSearch] = useState("");

  const {
    data: citiesResponse,
    mutate,
    isLoading,
  } = useSWR<ExtendedResponse<CitiesRes>>(
    `${apis.hotel.cities.client}?q=`,
    fetcher,
    {
      revalidateOnMount: true,
      revalidateIfStale: false,
      revalidateOnFocus: true,
      revalidateOnReconnect: false,
    }
  );

  const { data: cities } = citiesResponse ?? {};

  const findCity =
    search.length > 0
      ? (cities?.results?.filter((city) =>
          [city.fa_name, city.name, city.iata].some((field) =>
            field.toLowerCase().includes(search.toLowerCase())
          )
        ) as TCity[])
      : [];

  const {
    tabs: {
      hotel: {
        locationPicker: {
          locations: { domestic },
        },
      },
    },
  } = searchtabData;

  const form = useForm<HotelSchema>({
    resolver: yupResolver(hotelSchema),
    defaultValues: {
      checkin: checkIn
        ? checkIn?.replaceAll("-", "/")
        : hotelDefaultValue.checkin,
      checkout: checkOut
        ? checkOut?.replaceAll("-", "/")
        : hotelDefaultValue.checkout,
      rooms: rooms
        ? createRoomsPattern("decode", rooms)
        : hotelDefaultValue.rooms,
      city: hotelDefaultValue.city,
    },
  });

  const { watch, setValue, getValues } = form;

  const { checkin, checkout } = getValues();

  const startDate = parse(checkin, "yyyy/MM/dd", new Date());

  const endDate = parse(checkout, "yyyy/MM/dd", new Date());

  const initialDifference = differenceInDays(endDate, startDate);

  const checkInDate = watch("checkin");

  const progressInputs = [
    {
      id: 0,
      name: "city",
      value: watch("city.name"),
    },
    {
      id: 1,
      name: "checkin",
      value: watch("checkin"),
    },
    {
      id: 2,
      name: "checkout",
      value: watch("checkout"),
    },
    {
      id: 3,
      name: "rooms",
      value: Object.values(watch("rooms")).find((item) => !!item),
    },
  ];

  const { handleProgress, openStatus } = useProgress(progressInputs);

  const handleSetValues =
    ({
      paramName,
      id,
      func,
    }: {
      paramName: TFormField;
      id: number;
      func?: (value: any) => string;
    }) =>
    (value: any) => {
      if (func) {
        if (isNumber(id) && id === 1) {
          setValue(
            "checkout",
            format(addDays(value.depart, initialDifference), "yyyy/MM/dd")
          );
          setValue(
            paramName,
            func(
              typeof value === "string"
                ? value
                : (value as { depart: string }).depart
            )
          );
        }
      } else {
        setValue(paramName, value);
      }

      id !== 4 && handleProgress(id, true);
      search && setSearch("");
    };

  const icons: {
    [key: string]: ReactNode;
  } = {
    city: <MapPinSimpleArea className="w-5 h-5" />,
    checkin: <CalendarBlank className="w-5 h-5" />,
    checkout: <CalendarCheck className="w-5 h-5" />,
    rooms: <CaretDown className="w-5 h-5" />,
  };

  const inputs = (
    key: string,
    paramName: TFormField,
    id: number,
    func?: (value: unknown) => string
  ) => {
    const inputObject = {
      city: (
        <LocationPicker
          formName="hotel"
          onClick={handleSetValues({ paramName, id, func })}
          searchResult={{
            data: findCity,
            isLoading,
            search,
            setSearch: (e: React.ChangeEvent<HTMLInputElement>) =>
              setSearch(e.target.value),
          }}
        />
      ),
      checkin: (
        <Datepicker
          formatDateIntoJalali={false}
          onPickDate={handleSetValues({ paramName, id, func })}
          OnCloseComp={userDeviceType === "mobile" ? DrawerClose : PopoverClose}
          selectedDate={{
            from: checkInDate
              ? new Date(jalaliToGregorian(checkInDate))
              : undefined,
            to: checkInDate
              ? new Date(jalaliToGregorian(checkInDate))
              : undefined,
          }}
        />
      ),
      checkout: (
        <Stayduration
          initialDate={parse(checkin, "yyyy/MM/dd", new Date())}
          onClick={handleSetValues({ paramName, id, func })}
        />
      ),
      rooms: (
        <RoomPicker
          roomData={getValues("rooms")}
          OnCloseComp={userDeviceType === "mobile" ? DrawerClose : PopoverClose}
          onClick={handleSetValues({ paramName, id, func })}
          service="hotel"
        />
      ),
    };

    return inputObject[key as TFormField];
  };

  const onSubmit = (data: HotelSchema) => {
    const {
      city: { code, name },
      rooms,
    } = data;
    localStorage.setItem("drawerOpen", JSON.stringify(false));
    router.push(
      `/hotel/${code}/${slug ?? ""}/?checkIn=${format(
        startDate,
        "yyyy-MM-dd"
      )}&name=${name}&checkOut=${format(
        endDate,
        "yyyy-MM-dd"
      )}&rooms=${createRoomsPattern("encode", rooms)}`
    );
  };

  // set input values using searchParams
  useEffect(() => {
    const resutls = cities?.results;

    if (cities?.ok && resutls && searchParams) {
      setValue("city", {
        name: resutls.find((cit) => cit.iata === city)?.fa_name,
        code: resutls.find((cit) => cit.iata === city)?.iata,
      });
    } else if (searchParams) {
      setValue("city", {
        name: domestic.find((cit) => cit.id === city)?.lang_name,
        id: city,
        code: city,
      });
    }
  }, [cities, city, domestic, searchParams, setValue]);

  useEffect(() => {
    if (page === "result") {
      mutate();
    }
  }, [mutate, page]);

  return (
    <TabContent<TFormField, HotelSchema>
      mobile={mobile ?? false}
      page={page}
      className={(id, type) =>
        `${
          id === 0
            ? "col-span-12 sm:col-span-6 lg:col-span-3"
            : id === 3
            ? "col-span-12 sm:col-span-6 lg:col-span-3"
            : "col-span-12 lg:col-span-2"
        } ${type !== "origin" && type !== "destination" && "px-1"} ${
          type === "destination" && "pl-2"
        }`
      }
      services="hotel"
      inputConfig={{
        icons,
        inputs,
        progressInputs: { handleProgress, openStatus },
      }}
      // @ts-ignore
      form={form}
      // @ts-ignore
      onSubmit={onSubmit}
    />
  );
}
